import React from 'react';
import { createMemoryHistory, createBrowserHistory, createHashHistory } from 'history';
import { makeAutoObservable } from 'mobx';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { pathToRegexp, compile } from 'path-to-regexp';

var history = typeof document === 'undefined' ? createMemoryHistory() : createBrowserHistory();
function useHashBasedHistory() {
  history = createHashHistory();
}

var Link = ({
  to,
  children
}) => {
  function pushHistory(e) {
    e.preventDefault();
    history.push(to);
  }

  return /*#__PURE__*/React.createElement("a", {
    href: "",
    onClick: pushHistory
  }, children);
};

class RouteCollection {
  constructor(incomingRoutes) {
    this.routes = void 0;
    this.routes = incomingRoutes;
  }

  findMatch(url) {
    return Object.entries(this.routes).find(([view, matcher]) => matcher.matches(url));
  }

  getUrlForView(viewName, args = {}) {
    var matcher = this.routes[viewName];

    if (matcher === undefined) {
      throw new Error("View '".concat(viewName, " Does not exist}"));
    }

    return matcher.getUrl(args);
  }

  getViewForUrl(url) {
    var _ref = this.findMatch(url) || [undefined],
        _ref2 = _slicedToArray(_ref, 1),
        view = _ref2[0];

    return view;
  }

  getArgsForView(url) {
    var view = this.getViewForUrl(url);

    if (!view) {
      return {};
    }

    var matcher = this.routes[view];
    return matcher.getArgs(url);
  }

}

class Router {
  constructor(routes, baseUrl = '') {
    this.currentUrl = '';
    this.baseUrl = '';
    this.routes = void 0;

    this.listenerDisposer = () => {};

    this.routes = new RouteCollection(routes);
    this.baseUrl = baseUrl;
    makeAutoObservable(this, {
      'listenerDisposer': false
    });
  }

  setBaseUrl(url) {
    this.baseUrl = url;
    return this;
  }

  get parameters() {
    return this.routes.getArgsForView(this.currentUrl);
  }

  getParams(view) {
    if (view !== this.currentView) {
      throw Error("Cannot get route parameters for view '".concat(view, "' because current view is '").concat(this.currentView, "'"));
    }

    return this.parameters;
  }

  setCurrentUrl(url) {
    this.currentUrl = url.startsWith(this.baseUrl) ? url : "".concat(this.baseUrl).concat(url);
  }

  setCurrentView(view, ...params) {
    var args = params[0] || {};

    if (view === this.currentView && Object.keys(args).length === 0) {
      return;
    }

    var url = this.routes.getUrlForView(view, args);
    this.setCurrentUrl(url);
  }

  get currentView() {
    if (!this.currentUrl || this.currentUrl.length === 0) {
      return undefined;
    }

    if (this.currentUrl !== history.location.pathname) {
      history.push(this.currentUrl);
    }

    return this.routes.getViewForUrl(this.currentUrl);
  }

  start() {
    this.listenerDisposer = history.listen(update => this.setCurrentUrl(update.location.pathname));
    this.setCurrentUrl(history.location.pathname);
    return this;
  }

  stop() {
    this.listenerDisposer();

    this.listenerDisposer = () => {};

    return this;
  }

}

class RouteMatcher {
  constructor(urlPattern) {
    this.urlPattern = urlPattern;
    this.keys = [];
    this.patternRegex = void 0;
    this.compiledPattern = void 0;
    this.patternRegex = pathToRegexp(urlPattern, this.keys, {
      end: true,
      start: false
    });
    this.compiledPattern = compile(urlPattern);
  }

  matches(url) {
    return this.patternRegex.test(url);
  }

  getArgs(url) {
    var matches = this.patternRegex.exec(url);

    if (matches === null) {
      return {};
    }

    var params = {};
    this.keys.forEach((key, i) => {
      params[key.name] = matches[i + 1];
    });
    return params;
  }

  getUrl(args) {
    return this.compiledPattern(args || {});
  }

}
function toUrl(urlPattern) {
  return new RouteMatcher(urlPattern);
}

function routeNavigate(e) {
  e.preventDefault();
  history.push(e.currentTarget.href);
}

export { Link, Router, history, routeNavigate, toUrl, useHashBasedHistory };
